import { z } from "zod";

const text = (max: number) => z.string().trim().min(1, "Required").max(max, `Use at most ${max} characters`);
export const smbHttpsUrlSchema = text(2048).url("Enter a valid HTTPS URL").refine(value => {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password;
  } catch { return false; }
}, "Use HTTPS without embedded credentials");
export const smbHexSchema = text(7).regex(/^#?(?:[0-9a-f]{3}|[0-9a-f]{6})$/i, "Enter a 3 or 6 digit HEX color")
  .transform(value => {
    const hex = value.replace("#", "");
    return `#${hex.length === 3 ? [...hex].map(c => c + c).join("") : hex}`.toUpperCase();
  });
const fileKey = text(512).regex(/^[a-zA-Z0-9][a-zA-Z0-9/_-]*\.[a-zA-Z0-9]{2,8}$/, "Enter an uploaded file key")
  .refine(value => !value.includes(".."), "Invalid uploaded file key");
export const smbImageSourceSchema = z.union([smbHttpsUrlSchema, fileKey]);
const phone = text(100).regex(/^\+?[\d\s().-]+(?:\s*(?:x|ext\.?)\s*\d+)?$/i, "Enter a valid phone")
  .refine(value => value.replace(/\D/g, "").length >= 7, "Enter a valid phone");
export const smbPublicContactSchema = z.object({
  phone: phone.optional(), email: text(254).email("Enter a valid email").optional(),
  address: text(1000).optional(), hours: text(2000).optional(),
}).strict();
export const smbPrimaryCtaSchema = z.object({
  type: z.enum(["phone", "booking", "quote_form"]), label: text(255).optional(),
  url: smbHttpsUrlSchema.optional(), phone: phone.optional(),
}).strict().superRefine((value, ctx) => {
  if (value.type === "phone" ? !value.phone : !value.url)
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: [value.type === "phone" ? "phone" : "url"],
      message: value.type === "phone" ? "Provide this CTA's phone" : "Provide this CTA's HTTPS destination" });
});
// Quotes and attribution are deliberately not trimmed or rewritten.
const verbatim = (max: number) => z.string().min(1).max(max).refine(value => !!value.trim(), "Required");
export const smbProofSchema = z.object({
  googleRating: z.number().finite().min(0).max(5).optional(),
  reviewCount: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).optional(),
  yearsInBusiness: z.number().finite().min(0).max(Number.MAX_SAFE_INTEGER).optional(),
  licensed: z.boolean().optional(),
  testimonials: z.array(z.object({ quote: verbatim(2000), attribution: verbatim(255) }).strict()).max(6).optional(),
}).strict();
const socialsShape = Object.fromEntries(
  ["facebook", "instagram", "tiktok", "youtube", "google_business", "linkedin", "x", "yelp", "nextdoor"]
    .map(key => [key, smbHttpsUrlSchema.optional()]),
) as Record<"facebook" | "instagram" | "tiktok" | "youtube" | "google_business" | "linkedin" | "x" | "yelp" | "nextdoor", z.ZodOptional<typeof smbHttpsUrlSchema>>;
const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD")
  .refine(value => Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value, "Enter a valid date");
export const smbIntakeSchema = z.object({
  externalId: text(160).min(8, "Use at least 8 characters"),
  packageSlug: text(160).optional(),
  contact: z.object({ email: text(254).email("Enter a valid email"), name: text(255) }).strict(),
  brand: z.object({ name: text(255), url: smbHttpsUrlSchema.optional() }).strict(),
  logos: z.array(smbImageSourceSchema).max(5, "Use at most 5 logos"),
  colors: z.array(smbHexSchema).min(1, "Provide at least one color").max(8),
  services: z.array(z.object({ name: text(255), description: text(4000).optional(), url: smbHttpsUrlSchema.optional() }).strict()).min(1, "Provide at least one service").max(20),
  serviceAreas: z.array(text(255)).min(1, "Provide at least one service area").max(30),
  promotions: z.array(z.object({ title: text(255), details: text(4000).optional(), code: text(100).optional(),
    startsOn: date.optional(), endsOn: date.optional() }).strict().refine(
    p => !p.startsOn || !p.endsOn || p.startsOn <= p.endsOn, "End date must follow start date")).max(10),
  socials: z.object({ ...socialsShape, other: z.array(smbHttpsUrlSchema).max(10).optional() }).strict(),
  idealCustomer: z.object({ summary: text(4000).optional(), location: text(255).optional(),
    ageRange: text(100).optional(), painPoints: z.array(text(1000)).max(20).optional(), notes: text(4000).optional() }).strict()
    .refine(value => Object.values(value).some(v => Array.isArray(v) ? v.length > 0 : !!v), "Provide ideal-customer context"),
  localCompetitors: z.array(z.object({ name: text(255), url: smbHttpsUrlSchema.optional() }).strict()).max(10),
  referenceImages: z.array(smbHttpsUrlSchema).max(3).optional(),
  notes: text(8000).optional(),
  publicContact: smbPublicContactSchema.optional(),
  primaryCta: smbPrimaryCtaSchema.optional(),
  proof: smbProofSchema.optional(),
  tone: text(255).optional(),
  claimsToAvoid: text(2000).optional(),
  workPhotos: z.array(smbImageSourceSchema).max(12).optional(),
}).strict();
export type SmbIntake = z.infer<typeof smbIntakeSchema>;
export const SMB_INTAKE_BODY_LIMIT = "128kb";
export const smbIntakeReviewFlagSchema = z.enum(["missing_primary_logo", "no_default_package", "package_unavailable", "package_paused"]);
export type SmbIntakeReviewFlag = z.infer<typeof smbIntakeReviewFlagSchema>;
